import { Injectable } from '@angular/core';
import { UserService } from './user.service';
import { Subject, SUBJECTFIXEDFIELDS } from '../models/subject';
import { User } from '../models/user';
import { SubjectTypeService } from './subject-type.service';
import { CategoryService } from './category.service';
import { LookupService } from './lookup.service';
import { RelationshipService } from './relationship.service';
import { StatusService } from './status.service';
import { MarkerService } from './marker.service';
import { AssessmentTemplateService } from './assessment-template.service';
import { AssessmentTypeService } from './assessment-type.service';
import { ReferralSourceService } from './referral-source.service';
import { forkJoin, Observable, of } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { Category } from '../models/lookup-category';
import { SubjectType } from '../models/subject-type';
import { OrganisationService } from './organisation.service';

@Injectable({ providedIn: 'root'})
export class HydrateSubjectService{

  constructor(
    private subjectTypeService: SubjectTypeService,
    private categoryService: CategoryService,
    private lookupService: LookupService,
    private userService: UserService,
    private relationshipService: RelationshipService,
    private statusService: StatusService,
    private markerService: MarkerService,
    private assessmentTemplateService: AssessmentTemplateService,
    private assessmentTypeService: AssessmentTypeService,
    private organisationService: OrganisationService,
    private referralSourceService: ReferralSourceService) {}

    hydrate(item: Subject, level: number = 0): Observable<Subject> {
      const hydrate$ = [];
  
      if (item){

        item.isReferral = item.isReferral || false;
        item.hasReferral = item.subjectReferrals && (item.subjectReferrals.length > 0);

        if ((item.categoryId) && ((!item.category) || (item.category.id !== item.categoryId))){
          hydrate$.push(this.categoryService.getById(item.categoryId).pipe(take(1), map((category) => {
            item.category = new Category(category);
            return item;
          })));
        }
  
        if ((item.subjectTypeId) && ((!item.subjectType) || (item.subjectType.id !== item.subjectTypeId))){
          hydrate$.push(this.subjectTypeService.getById(item.subjectTypeId).pipe(take(1), map((subjectType) => {
            item.subjectType = new SubjectType(subjectType);
            item.isPrimary = item.isPrimary || item.subjectType.primary;
            return item;
          })));
        }
  
        // check if we have subject data.  If we do then we need to populate the displayValue property
        if (item.subjectData){
          item.subjectData.displayValues = {};
          if (item.subjectData.data){
            // get subject type
            hydrate$.push(this.subjectTypeService.getById(item.subjectTypeId).pipe(take(1), map((subjectType) => {
              // iterate over subject data fields - the subject type gives us the possible fields
              subjectType.dataFields.forEach((field) => {
                // check if is a custom field in the subject data - if it is then check if we need to lookup the display value
                if (SUBJECTFIXEDFIELDS.indexOf(field.name.toLowerCase()) < 0){
                  let itemData = item.subjectData.data[field.id];
                  if (itemData === null){ itemData = undefined; }
                  if (itemData !== undefined){
                    let displayValue = itemData;
                    if (itemData && (field.dataType.toLowerCase() === 'status')){
                      const statusId = (itemData.length > 6) ? itemData.slice(6) : itemData;
                      displayValue = this.statusService.getDescription(Number(statusId));
                      item.subjectData.statusColor = this.statusService.getColor(Number(statusId));
                    }
                    else{
                      if ((/^\d+$/.test(field.dataType)) && (/^\d+$/.test(itemData)) && (!isNaN(itemData))){
                        displayValue = this.lookupService.getDescription(Number(itemData));
                      }
                      if (itemData && (field.dataType.toLowerCase() === 'user')){
                        if ((/^\d+$/.test(itemData)) && (!isNaN(itemData))){
                          const user = this.userService.getUserById(Number(itemData));
                          if (user){
                            displayValue = new User(user).fullName;
                          }
                        }
                      }
                    }
                    item.subjectData.displayValues[field.id] = displayValue;
                  }
                }
                return item;
              });
            })));
          }
        }
  
        // createdBy
        if ((item.createdById) && ((!item.createdBy) || (item.createdBy.id !== item.createdById))){
          hydrate$.push(this.userService.getById(item.createdById).pipe(take(1), map((user) => {
            item.createdBy = new User(user);
            return item;
          })));
        }
  
        // user
        if ((item.subjectUsers) && (item.subjectUsers.length > 0) && (item.subjectUsers[0].userId)
        && ((!item.subjectUsers[0].user) || (item.subjectUsers[0].user.id !== item.subjectUsers[0].userId) || (!item.subjectUsers[0].user.fullName))){
          hydrate$.push(this.userService.getById(item.subjectUsers[0].userId).pipe(take(1), map((user) => {
            item.subjectUsers[0].user = new User(user);
            return item;
          })));
        }

        // manager
        if ((item.subjectUsers) && (item.subjectUsers.length > 0) && (item.subjectUsers[0].managerId)
        && ((!item.subjectUsers[0].manager) || (item.subjectUsers[0].manager.id !== item.subjectUsers[0].managerId) || (!item.subjectUsers[0].manager.fullName))){
          hydrate$.push(this.userService.getById(item.subjectUsers[0].managerId).pipe(take(1), map((user) => {
            item.subjectUsers[0].manager = new User(user);
            return item;
          })));
        }
  
        if ((level < 2) && (item.subjectLinks) && (item.subjectLinks.length > 0)){
          item.subjectLinks.sort((a, b) => a.id - b.id).map((link) => {
            hydrate$.push(this.relationshipService.getById(link.relationshipId).pipe(take(1), map((relationship) => {
              link.relationship = relationship;
            })));
            // call recursive hydrate to ensure we have everything setup for linked subjects
            if (link.linkedSubject) {
              link.linkedSubject.isReferral = item.isReferral;
              hydrate$.push(this.hydrate(link.linkedSubject, level + 1));
            }
          });
        }
  
        if ((item.subjectAssessments) && (item.subjectAssessments.length > 0)){
          item.subjectAssessments.map((subjectAssessment) => {
            if (subjectAssessment.assessment){
              const assessment = subjectAssessment.assessment;
              if (assessment){
  
                if (assessment.completedById && ((!assessment.completedBy) || (assessment.completedBy.id !== assessment.completedById))){
                  hydrate$.push(this.userService.getById(assessment.completedById).pipe(take(1), map((user) => {
                    assessment.completedBy = new User(user);
                    return item;
                  })));
                }
                if (assessment.assessmentTemplateId && ((!assessment.assessmentTemplate) || (assessment.assessmentTemplate.id !== assessment.assessmentTemplateId))){
                  hydrate$.push(this.assessmentTemplateService.getById(assessment.assessmentTemplateId).pipe(take(1), map((assessmentTemplate) => {
                    assessment.assessmentTemplate = assessmentTemplate;
                    return item;
                  })));
                }
                if (assessment.assessmentAssessmentTypeLinks && assessment.assessmentAssessmentTypeLinks.length > 0){
                  assessment.assessmentAssessmentTypeLinks.forEach((link) => {
                    hydrate$.push(this.assessmentTypeService.getById(link.assessmentTypeId).pipe(take(1), map((typ) => {
                      link.assessmentType = typ;
                      return item;
                    })));
                  });
                }


              }
            }
          });
        }
  
        if ((item.subjectMarkers) && (item.subjectMarkers.length > 0)){
          item.subjectMarkers.map((subjectMarker) => {
            hydrate$.push(this.markerService.getById(subjectMarker.markerId).pipe(take(1), map((marker) => {
              subjectMarker.marker = marker;
              return item;
            })));
          });
        }

        if ((item.subjectOrganisations) && (item.subjectOrganisations.length > 0)){
          item.subjectOrganisations.map((subjectOrganisation) => {
            hydrate$.push(this.organisationService.getById(subjectOrganisation.organisationId).pipe(take(1), map((organisation) => {
              subjectOrganisation.organisation = organisation;
              return item;
            })));
          });
        }
  
        if (item.subjectReferrals && (item.subjectReferrals.length > 0)){
          // the default referral for the subject is the first referral, used by the referral widget etc
          item.referral = item.subjectReferrals[0].referral;
  
          item.subjectReferrals.map((subjectReferral) => {
            if (subjectReferral.referral){
              const referral = subjectReferral.referral;
              if (referral && referral.referralSourceId && ((!referral.referralSource) || (referral.referralSource.id !== referral.referralSourceId))){
                hydrate$.push(this.referralSourceService.getById(referral.referralSourceId).pipe(take(1), map((referralSource) => {
                  referral.referralSource = referralSource;
                  return item;
                })));
              }
            }
          });
        }
  
        if ((item.subjectReviews) && (item.subjectReviews.length > 0)){
          item.subjectReviews.map((subjectReview) => {
            if (subjectReview.review){
              const review = subjectReview.review;
              if (review && review.reviewedById && ((!review.reviewedBy) || (review.reviewedBy.id !== review.reviewedById))){
                hydrate$.push(this.userService.getById(review.reviewedById).pipe(take(1), map((user) => {
                  review.reviewedBy = new User(user);
                  return item;
                })));
              }
            }
          });
        }
      }
      
      if (hydrate$.length > 0){
        return forkJoin(hydrate$).pipe(take(1), map((_) => {
          // sort subject markers by name...
          if ((item.subjectMarkers) && (item.subjectMarkers.length > 1)){
            item.subjectMarkers.sort((a , b) => a.marker.name.localeCompare(b.marker.name));
          }
          // sort subject markers of linked subjects by name...
          if ((item.subjectLinks) && (item.subjectLinks.length > 0)){
            item.subjectLinks.map((link) => {
              if (link.linkedSubject && link.linkedSubject.subjectMarkers && link.linkedSubject.subjectMarkers.length > 1){
                link.linkedSubject.subjectMarkers.sort((a , b) => a.marker.name.localeCompare(b.marker.name));
              }
            });
          }
          return item;
        }));
      }
      else{
        return of(item);
      }
    }
  
    // MRD - lookup objects, such as markers, users etc need to be stripped so we don't get issues with EF tracking conflicts on save
    public dehydrate(subject: Subject): Subject{
      subject.category = null;
      subject.subjectType = null;
      subject.createdBy = null;
      (subject.subjectData as any).subjectType = null;
      
      if (subject.subjectUsers && subject.subjectUsers.length > 0){
        subject.subjectUsers[0].user = null;
        subject.subjectUsers[0].manager = null;
      }
  
      if (subject.subjectAssessments && (subject.subjectAssessments.length > 0)){
        subject.subjectAssessments.map((subjectAssessment) => {

          if (subjectAssessment.assessment){
            if (subjectAssessment.assessment.assessmentAssessmentTypeLinks && (subjectAssessment.assessment.assessmentAssessmentTypeLinks.length > 0)){
              subjectAssessment.assessment.assessmentAssessmentTypeLinks.map((link) => {
              link.assessmentType = null;
              });
            }
            if (subjectAssessment.assessment.assessmentTemplateId){
              subjectAssessment.assessment.assessmentTemplate = null;
            }
            if (subjectAssessment.assessment.completedById){
              subjectAssessment.assessment.completedBy = null;
            }

            if(subjectAssessment.assessment.reviewId){
              subjectAssessment.assessment.review = null;
            }else{
              if(subjectAssessment.assessment.review && subjectAssessment.assessment.review.reviewedById){
                subjectAssessment.assessment.review.reviewedBy = null;
              }
            }
          }
        });
      }
      if (subject.subjectReviews && subject.subjectReviews.length > 0){
        subject.subjectReviews.map((subjectReview) => {
          if (subjectReview.reviewId){
            subjectReview.review = null;
          }else{
            if(subjectReview.review && subjectReview.review.reviewedById ){
              subjectReview.review.reviewedBy = null;
            }
          }
        });
      }
      if (subject.subjectMarkers && subject.subjectMarkers.length > 0){
        subject.subjectMarkers.map((subjectMarker) => {
          subjectMarker.marker = null;
        });
      }
      if (subject.subjectOrganisations && subject.subjectOrganisations.length > 0){
        subject.subjectOrganisations.map((subjectOrganisation) => {
          subjectOrganisation.organisation = null;
        });

        // MRD: a subject can only have zero or one organisation associated with it so if we have multiple records - remove them
        if (subject.subjectOrganisations.length > 1){
          subject.subjectOrganisations = [subject.subjectOrganisations[0]];
        }
      }
      if (subject.subjectReferrals && subject.subjectReferrals.length > 0){
        subject.subjectReferrals.map((subjectReferral) => {
          if (subjectReferral.referral){
            subjectReferral.referral.referralSource = null;
          }
        });
      }
      if ((subject.subjectLinks) && (subject.subjectLinks.length > 0)){
        subject.subjectLinks.map((link) => {
          if (link.relationshipId) {
            link.relationship = null;
          }
          // call recursive dehydrate to ensure we don't trip up EntityFramework
          if (link.linkedSubject){
            link.linkedSubject = this.dehydrate(link.linkedSubject);
          }
        });
      }

      return subject;
    }
}
